import React, { useEffect, useRef, useState } from "react";

export interface MenuItem {
  label: string;
  onClick: () => void;
}

export enum DropdownIconType {
  DOTS = "dots",
  CHEVRON = "chevron",
}

interface DropdownMenuProps {
  menuItems: MenuItem[];
  className?: string;
  iconType?: DropdownIconType;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  menuItems,
  className = "",
  iconType = DropdownIconType.DOTS,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      menuOpen &&
      menuRef.current &&
      !menuRef.current.contains(event.target as Node)
    ) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const handleMenuAction = () => {
    setMenuOpen(!menuOpen);
  };

  const handleItemClick = (onClick: () => void) => {
    onClick();
    setMenuOpen(false);
  };

  const getIcon = () => {
    switch (iconType) {
      case DropdownIconType.CHEVRON:
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="white"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        );
      case DropdownIconType.DOTS:
      default:
        return (
          <svg
            className="w-5 h-5"
            fill="white"
            stroke="white"
            viewBox="0 0 24 24"
          >
            <circle cx="5" cy="12" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="19" cy="12" r="1.5" />
          </svg>
        );
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} ref={menuRef}>
      <button
        type="button"
        className="text-white/60 hover:text-white transition-colors"
        onClick={handleMenuAction}
        aria-label="Menu"
      >
        {getIcon()}
      </button>

      {menuOpen && (
        <div className="absolute right-0 mt-2 w-max bg-[#191919] text-white rounded-lg shadow-lg overflow-hidden z-10">
          {menuItems.map((item, index) => (
            <React.Fragment key={item.label}>
              <button
                type="button"
                className="flex items-center w-full px-4 py-2 hover:bg-white/5 transition-colors whitespace-nowrap"
                onClick={() => handleItemClick(item.onClick)}
              >
                {item.label}
              </button>
              {index < menuItems.length - 1 && (
                <div className="h-px bg-white/10" />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

DropdownMenu.defaultProps = {
  className: "",
  iconType: DropdownIconType.DOTS,
};

export default DropdownMenu;
